import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonModule } from "primeng/button";
import { ButtonGroupModule } from "primeng/buttongroup";
import { CardModule } from "primeng/card";
import { PanelModule } from "primeng/panel";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { RatingModule } from "primeng/rating";
import { TooltipModule } from "primeng/tooltip";

import { MzTabResult } from "../../../../modules/lipidcompass-backend-client";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { CvparameterClassificationComponent } from "../../layouts/cvparameter-classification/cvparameter-classification.component";
import { OlslinkComponent } from "../../olslink/olslink.component";
import { DatasetDetailPlotsComponent } from "../dataset-detail-plots/dataset-detail-plots.component";

export class CurationLevel {
  value: number = 1;
  label: MzTabResult.CurationLevelEnum =
    MzTabResult.CurationLevelEnum.UNCHECKED;
  ncases: number = Object.keys(MzTabResult.CurationLevelEnum).length - 1;
}

@Component({
  selector: "app-dataset-card",
  templateUrl: "./dataset-card.component.html",
  styleUrls: ["./dataset-card.component.css"],
  standalone: true,
  imports: [
    ButtonGroupModule,
    ButtonModule,
    CardModule,
    CommonModule,
    CvparameterClassificationComponent,
    DatasetDetailPlotsComponent,
    FormsModule,
    OlslinkComponent,
    PanelModule,
    ProgressSpinnerModule,
    RatingModule,
    RouterModule,
    TooltipModule,
    TranslateModule
  ]
})
export class DatasetCardComponent implements OnChanges {
  private loggingService = inject(LoggingService);

  @Input() mzTabResult: MzTabResult;

  @Input() detailView: boolean = false;

  @Input() showClassifications: boolean = true;

  @Input() showDatasetLink: boolean = true;

  @Input() showPlots: boolean = true;

  @Input() toggleable: boolean = true;

  /** Renders the page-level help + feature-tour buttons in the detail header.
   * Off by default: this card is shared, and only the routed dataset detail
   * page has a help drawer and a tour to wire the clicks to — the submission
   * page renders the same detail view and must stay unchanged. */
  @Input() showHelpControls: boolean = false;

  /** The help button was clicked; the host page opens its help drawer. */
  @Output() helpClick = new EventEmitter<void>();

  /** The tour button was clicked; the host page starts its Shepherd tour. */
  @Output() tourClick = new EventEmitter<void>();

  public curationLevel: CurationLevel = new CurationLevel();

  ngOnChanges(changes: SimpleChanges): void {
    this.loggingService.log(
      "DatasetCardComponent",
      "mzTabResult: " + JSON.stringify(this.mzTabResult),
      LogLevel.DEBUG
    );
    if (changes?.mzTabResult?.currentValue) {
      if (this.mzTabResult?.curationLevel) {
        this.curationLevel.label = this.mzTabResult
          ?.curationLevel as MzTabResult.CurationLevelEnum;
      }
      switch (this.mzTabResult?.curationLevel) {
        case MzTabResult.CurationLevelEnum.UNCHECKED:
          this.curationLevel.value = 1;
          break;
        case MzTabResult.CurationLevelEnum.AUTOMATICALLY_CHECKED:
          this.curationLevel.value = 2;
          break;
        case MzTabResult.CurationLevelEnum.MANUALLY_CURATED:
          this.curationLevel.value = 3;
          break;
        case MzTabResult.CurationLevelEnum.REFERENCE:
          this.curationLevel.value = 4;
          break;
        default:
          this.curationLevel.value = 0;
          this.loggingService.log(
            "DatasetCardComponent",
            "Unknown curationLevel: " + this.mzTabResult?.curationLevel,
            LogLevel.ERROR
          );
          break;
      }
      if (!this.detailView || !this.showPlots) {
        this.loggingService.log(
          "DatasetCardComponent",
          "Not loading plot data. Set 'detailView=true and showPlots=true' to enable plot data loading!",
          LogLevel.DEBUG
        );
      }
    }
  }
}
